import React, { useState, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import AgentSteps from '@/components/AgentSteps';
import { Copy, Check, SpeakerHigh, SpeakerSlash, Sparkle, PencilSimple, ArrowsClockwise, ArrowSquareOut, FloppyDisk, X, LockSimple, CloudCheck, Lightning } from '@phosphor-icons/react';

export default function ChatMessage({ message, onEdit, onRegenerate, onArtifact }) {
  const isUser = message.role === 'user';
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(message.content);

  const handleSaveEdit = () => {
    if (editText.trim() && editText !== message.content) {
      onEdit?.(message.id, editText.trim());
    }
    setEditing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className={`mb-5 group ${isUser ? 'flex justify-end' : ''}`}
      data-testid={`chat-message-${message.role}`}
    >
      {isUser ? (
        <div className="max-w-[75%]">
          {editing ? (
            <div className="space-y-2">
              <textarea
                value={editText}
                onChange={e => setEditText(e.target.value)}
                className="w-full min-h-[60px] px-4 py-3 text-[14px] text-white/90 bg-white/5 border border-lime-400/40 rounded-2xl rounded-tr-sm outline-none resize-none focus:ring-2 focus:ring-lime-500/20"
                data-testid="edit-message-input"
                autoFocus
              />
              <div className="flex justify-end gap-1.5">
                <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded-lg text-[12px] text-white/50 hover:bg-white/10 transition-colors" data-testid="edit-cancel-button">
                  <X className="w-3 h-3 inline mr-1" />Cancel
                </button>
                <button onClick={handleSaveEdit} className="px-3 py-1.5 rounded-lg text-[12px] font-medium bg-lime-400/100 hover:bg-lime-600 text-white transition-colors" data-testid="edit-save-button">
                  <FloppyDisk className="w-3 h-3 inline mr-1" />Save & resend
                </button>
              </div>
            </div>
          ) : (
            <div className="relative">
              <div className="bg-white/5 border border-white/10 rounded-2xl rounded-tr-sm px-4 py-3">
                {/* Image preview */}
                {(message.image_base64 || message.image_preview) && (
                  <img
                    src={message.image_base64 ? `data:image/png;base64,${message.image_base64}` : message.image_preview}
                    alt="Attached"
                    className="max-w-full max-h-48 rounded-lg mb-2 object-cover"
                  />
                )}
                <p className="text-[14px] leading-relaxed text-white/90 whitespace-pre-wrap">{message.content}</p>
              </div>
              {onEdit && !message.id?.startsWith('t-') && (
                <button
                  onClick={() => { setEditText(message.content); setEditing(true); }}
                  className="absolute -left-8 top-2 w-6 h-6 rounded-md flex items-center justify-center text-white/40 hover:text-white/80 hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-all"
                  data-testid="edit-message-button"
                >
                  <PencilSimple className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="w-full">
          {message.agent_steps && <AgentSteps steps={message.agent_steps} />}
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-md bg-lime-400/100 flex items-center justify-center shrink-0 mt-0.5">
              <Sparkle weight="fill" className="w-3 h-3 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="mb-1">
                {message.processed_locally ? (
                  (message.local_model || '').includes('Heavy') ? (
                    <span data-testid="route-badge-heavy" className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-lime-400/15 to-orange-400/15 text-lime-300 border border-lime-400/30">
                      <Lightning weight="fill" className="w-2.5 h-2.5 text-orange-400" />
                      {message.local_model} · On-Device
                    </span>
                  ) : (
                    <span data-testid="route-badge-local" className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-400/10 text-emerald-400 border border-emerald-400/20">
                      <LockSimple weight="fill" className="w-2.5 h-2.5" />
                      On-Device{message.local_model ? ` · ${message.local_model}` : ''}
                    </span>
                  )
                ) : (
                  <span data-testid="route-badge-cloud" className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-white/40 border border-white/10">
                    <CloudCheck weight="fill" className="w-2.5 h-2.5" />
                    Private Cloud
                  </span>
                )}
              </div>
              <div className="msg-md text-[14px] leading-relaxed text-white/90">
                <MdContent content={message.content} onArtifact={onArtifact} />
                {message.streaming && <span className="inline-block w-1.5 h-4 bg-lime-400/100 ml-0.5 animate-pulse rounded-sm" />}
              </div>
              {!message.streaming && (
                <div className="flex items-center gap-0.5 mt-1.5">
                  <CopyBtn text={message.content} />
                  <SpeakBtn text={message.content} />
                  {onRegenerate && !message.id?.startsWith('e-') && (
                    <button
                      onClick={() => onRegenerate?.(message.id)}
                      className="w-7 h-7 rounded-md flex items-center justify-center text-white/40 hover:text-white/80 hover:bg-white/10 transition-all"
                      data-testid="regenerate-button"
                    >
                      <ArrowsClockwise className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

function MdContent({ content, onArtifact }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}
      components={{
        code({ node, inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '');
          const lang = match ? match[1] : '';
          if (!inline && (lang || String(children).includes('\n'))) {
            return <CodeBlock lang={lang || 'text'} onArtifact={onArtifact}>{String(children).replace(/\n$/, '')}</CodeBlock>;
          }
          return <code {...props}>{children}</code>;
        },
        img({ src, alt }) {
          return <img src={src} alt={alt || ''} className="max-w-full rounded-lg my-2 border border-white/10" loading="lazy" />;
        }
      }}
    >{content}</ReactMarkdown>
  );
}

function CodeBlock({ lang, children, onArtifact }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(children); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <div className="my-3 rounded-lg overflow-hidden border border-white/10 bg-[#0a0a0a]">
      <div className="flex items-center justify-between px-4 py-1.5 bg-gray-900 border-b border-gray-800">
        <span className="text-[10px] font-mono text-white/50 uppercase">{lang}</span>
        <div className="flex items-center gap-1">
          {onArtifact && (
            <button
              onClick={() => onArtifact({ language: lang, code: children, title: '' })}
              className="flex items-center gap-1 text-[10px] text-white/50 hover:text-white/60 transition-colors"
              data-testid="open-artifact-button"
            >
              <ArrowSquareOut className="w-3 h-3" /> Expand
            </button>
          )}
          <button onClick={copy} className="flex items-center gap-1 text-[10px] text-white/50 hover:text-white/60 transition-colors ml-2" data-testid="copy-code-button">
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
      <SyntaxHighlighter language={lang} style={oneDark}
        customStyle={{ margin: 0, padding: '0.875rem 1rem', fontSize: '13px', background: '#0a0a0a', border: 'none', lineHeight: '1.6' }}>
        {children}
      </SyntaxHighlighter>
    </div>
  );
}

function CopyBtn({ text }) {
  const [c, setC] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setC(true); setTimeout(() => setC(false), 2000); }}
      className="w-7 h-7 rounded-md flex items-center justify-center text-white/40 hover:text-white/80 hover:bg-white/10 transition-all"
      data-testid="copy-message-button">
      {c ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

function SpeakBtn({ text }) {
  const [on, setOn] = useState(false);
  const audioRef = useRef(null);
  const clean = text.replace(/[#*`\[\]()!]/g, '').replace(/\n+/g, '. ');

  const stop = () => {
    audioRef.current?.pause();
    window.speechSynthesis.cancel();
    setOn(false);
  };

  const speak = async () => {
    if (on) { stop(); return; }
    setOn(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const res = await axios.post(`${API}/voice/tts`, { text: clean.slice(0, 3900) }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setOn(false); URL.revokeObjectURL(url); };
      audio.onerror = () => setOn(false);
      await audio.play();
    } catch {
      const u = new SpeechSynthesisUtterance(clean);
      u.rate = 1; u.onend = () => setOn(false); u.onerror = () => setOn(false);
      window.speechSynthesis.speak(u);
    }
  };
  return (
    <button onClick={speak} className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${on ? 'text-lime-400 bg-lime-400/10' : 'text-white/40 hover:text-white/80 hover:bg-white/10'}`}
      data-testid="speak-message-button">
      {on ? <SpeakerSlash className="w-3 h-3" /> : <SpeakerHigh className="w-3 h-3" />}
    </button>
  );
}
